import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { useAllSystemConfig } from "@/hooks/use-system-config";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import { formatDateTimeShort } from "@/lib/format";

const FEATURE_FLAG_KEY_SET = new Set<string>(Object.keys(FEATURE_FLAGS));

function formatConfigValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return "—";
  }
}

/**
 * VIB-1396: «Driftskonfig»-seksjon i Funksjonsbrytere-panelet. Viser øvrige
 * system_config-nøkler (vekter, autosend-brytere, satser m.m.) read-only —
 * v1 trenger ikke redigering her siden disse allerede har egne
 * innstillingsflater der de kan endres (f.eks. Økonomi-fanen).
 */
export function OperationalConfigSection() {
  const { data: rows, isLoading } = useAllSystemConfig();
  const others = (rows ?? []).filter((r) => !FEATURE_FLAG_KEY_SET.has(r.key));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <SrIcon name="database" className="h-4 w-4 text-muted-foreground" />
          Driftskonfig
        </CardTitle>
        <CardDescription>
          Øvrige system_config-nøkler. Read-only visning i v1 — rediger via egne
          innstillingsflater der de finnes (f.eks. Økonomi-fanen).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
        )}
        {!isLoading && others.length === 0 && (
          <p className="text-sm text-muted-foreground">Ingen andre system_config-nøkler funnet.</p>
        )}
        {!isLoading && others.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-2 font-medium">Nøkkel</th>
                  <th className="text-left p-2 font-medium">Verdi</th>
                  <th className="text-left p-2 font-medium">Sist endret</th>
                </tr>
              </thead>
              <tbody>
                {others.map((row) => (
                  <tr key={row.key} className="border-t border-border/50">
                    <td className="p-2 font-mono text-xs">{row.key}</td>
                    <td className="p-2">{formatConfigValue(row.value)}</td>
                    <td className="p-2 text-muted-foreground">
                      {formatDateTimeShort(row.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
